/* eslint-disable react-hooks/exhaustive-deps */
import ContentShell from "components/ContentShell";
import React, { useEffect, useState, useCallback, useRef } from "react";
import { useLocation, useNavigate, useBlocker } from "react-router-dom";
import { Box, Button, Divider, Alert, Typography } from "@mui/material";
import configData from "config/config.json";
import { useSnackbar } from "hooks/useSnackbar";
import useStyles from "styles/operator/operatorEditor";
import {
  create,
  getOperatorInfo,
  update,
  updateOperatorVaultConfig,
} from "services/operatorService";
import sessionHelperInstance from "services/sessionHelper";
import {
  getBettingMarkets,
  getDefaultMarginInfo,
} from "services/bettingMarketService";
import { marketMarginValidation, operatorValidation } from "utils/validation";
import useOpenClose from "hooks/useOpenClose";
import ViewLanguageModal from "screens/modals/ViewLanguageModal";
import { getConfigurations } from "services/miscService";
import { userRoles } from "utils/constants";
import { calculateOverallRTP } from "utils/miscUtils";

// Import new components
import OperatorInformation from "./components/OperatorInformation";
import FeaturesSettings from "./components/FeaturesSettings";
import VaultConfiguration from "./components/VaultConfiguration";
import MarginLockToggle from "./components/MarginLockToggle";
import BetLimitsSection from "./components/BetLimitsSection";
import MarginRTPSection from "./components/MarginRTPSection";
import LockToggleConfirmDialog from "./components/LockToggleConfirmDialog";
import OperatorEditorSkeleton from "./components/OperatorEditorSkeleton";
import UnsavedChangesDialog from "./components/UnsavedChangesDialog";

const _betLimit = {
  currency: "INR",
  minBet: 1,
  maxBet: 10,
  maxPayOut: 1000,
};

const _operatorDetails = {
  operatorName: "",
  platform: "",
  externalOpId: null,
  operatorMargins: null,
  operatorRtp: null,
  coinPurchaseAllowed: false,
  isActive: true,
  betLimits: [_betLimit],
  isUnderMaintenance: false,
  underMaintenanceTitle: "",
  underMaintenanceTitleTrans: {},
  showWinLoss: false,
  showRtpGameRules: false,
  countryCode: null,
  languageCode: null,
  showWalletIcon: false,
  startMatchBtnRestriction: false,
  startMatchBtnDiv: null,
  divisionBaseOvrEnabled: true,
  matchesForDivisionBaseOvr: null,
  showNewFtue: false,
  isTutorialVideoEnabled: false,
  isStickyCtaEnabled: false,
  showOddsCalculatingAnimation: false,
  allowManualReconciliation: false,
};

const _vaultConfigDetails = {
  baseXP: "",
  growthRate: "",
};

const OperatorEditor = () => {
  const styles = useStyles();

  const location = useLocation();
  const { showSnackbar } = useSnackbar();
  const id = location.pathname.split("/").slice(-1)[0];
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true); // Track initial data loading
  const [vaultConfigSaveLoading, setVaultConfigSaveLoading] = useState(false);
  const [sectionLoading, setSectionLoading] = useState({
    information: false,
    features: false,
    betLimits: false,
    margins: false,
  });

  const [operatorDetails, setOperatorDetails] = useState(_operatorDetails);
  const [defaultMarginData, setDefaultMarginData] = useState(null); // Store default margin info for validation
  const [options, setOptions] = useState({
    bettingMarkets: [],
    bettingMarketOptions: [],
    selectedMarkets: [],
  });
  const [errors, setErrors] = useState({});
  const { isOpen, close, open } = useOpenClose();
  const [modalData, setModalData] = useState({
    language: "",
    inputValue: "",
    field: null,
    viewData: [],
  });
  const [lockToggleDialog, setLockToggleDialog] = useState({
    open: false,
    loading: false,
  });

  const [vaultConfigDetails, setVaultConfigDetails] =
    useState(_vaultConfigDetails);

  // Track initial values for unsaved changes detection
  const initialOperatorDetailsRef = useRef<any>(null);
  const initialVaultConfigRef = useRef<any>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [unsavedChangesDialog, setUnsavedChangesDialog] = useState({
    open: false,
    pendingNavigation: null as (() => void) | null,
  });

  // Track if user has made any manual changes (not automatic state updates)
  const userHasInteracted = useRef(false);
  const isDataLoaded = useRef(false);

  // Helper to find differences between two objects (for debugging)
  const findDifferences = useCallback(
    (obj1: any, obj2: any, path = ""): string[] => {
      const differences: string[] = [];

      if (obj1 === obj2) return differences;
      if (obj1 == null && obj2 == null) return differences;
      if (obj1 == null || obj2 == null) {
        differences.push(
          `${path}: ${JSON.stringify(obj1)} !== ${JSON.stringify(obj2)}`,
        );
        return differences;
      }
      if (typeof obj1 !== "object" || typeof obj2 !== "object") {
        if (obj1 !== obj2) {
          differences.push(
            `${path}: ${JSON.stringify(obj1)} !== ${JSON.stringify(obj2)}`,
          );
        }
        return differences;
      }

      const allKeys = new Set([...Object.keys(obj1), ...Object.keys(obj2)]);

      for (const key of allKeys) {
        const newPath = path ? `${path}.${key}` : key;
        if (!(key in obj1)) {
          differences.push(
            `${newPath}: missing in current (was ${JSON.stringify(obj2[key])})`,
          );
        } else if (!(key in obj2)) {
          differences.push(
            `${newPath}: new key (value: ${JSON.stringify(obj1[key])})`,
          );
        } else {
          differences.push(...findDifferences(obj1[key], obj2[key], newPath));
        }
      }

      return differences;
    },
    [],
  );

  // Deep comparison helper for objects
  const deepEqual = useCallback((obj1: any, obj2: any): boolean => {
    if (obj1 === obj2) return true;
    if (obj1 == null || obj2 == null) return obj1 === obj2;
    if (typeof obj1 !== "object" || typeof obj2 !== "object")
      return obj1 === obj2;

    const keys1 = Object.keys(obj1);
    const keys2 = Object.keys(obj2);

    if (keys1.length !== keys2.length) return false;

    for (const key of keys1) {
      if (!keys2.includes(key)) return false;
      if (!deepEqual(obj1[key], obj2[key])) return false;
    }

    return true;
  }, []);

  // Check for unsaved changes whenever operatorDetails or vaultConfigDetails change
  // Only check if user has interacted with the form
  useEffect(() => {
    if (
      initialLoading ||
      !initialOperatorDetailsRef.current ||
      !isDataLoaded.current
    )
      return;

    // Only check for changes if user has manually interacted
    if (!userHasInteracted.current) {
      return;
    }

    const operatorChanged = !deepEqual(
      operatorDetails,
      initialOperatorDetailsRef.current,
    );
    const vaultChanged =
      id !== "create" &&
      !deepEqual(vaultConfigDetails, initialVaultConfigRef.current);

    const hasChanges = operatorChanged || vaultChanged;

    // Debug logging - only log when there are changes
    // if (hasChanges) {
    //   console.group("🔍 Unsaved Changes Detected");
    //   if (operatorChanged) {
    //     console.log("Operator Details differences:");
    //     const opDiffs = findDifferences(
    //       operatorDetails,
    //       initialOperatorDetailsRef.current
    //     );
    //     opDiffs.forEach((diff) => console.log(`  - ${diff}`));
    //   }
    //   if (vaultChanged) {
    //     console.log("Vault Config differences:");
    //     const vaultDiffs = findDifferences(
    //       vaultConfigDetails,
    //       initialVaultConfigRef.current
    //     );
    //     vaultDiffs.forEach((diff) => console.log(`  - ${diff}`));
    //   }
    //   console.groupEnd();
    // }

    setHasUnsavedChanges(hasChanges);
  }, [
    operatorDetails,
    vaultConfigDetails,
    initialLoading,
    id,
    deepEqual,
    findDifferences,
  ]);

  // Block navigation when there are unsaved changes
  // Use useCallback to create a stable blocker function
  const shouldBlock = useCallback(
    ({
      currentLocation,
      nextLocation,
    }: {
      currentLocation: any;
      nextLocation: any;
    }) => {
      return (
        hasUnsavedChanges && currentLocation.pathname !== nextLocation.pathname
      );
    },
    [hasUnsavedChanges],
  );

  const blocker = useBlocker(shouldBlock);

  // Handle blocker state changes
  useEffect(() => {
    if (blocker.state === "blocked") {
      setUnsavedChangesDialog({
        open: true,
        pendingNavigation: () => {
          if (blocker.proceed) {
            blocker.proceed();
          }
        },
      });
    }
  }, [blocker.state, blocker.proceed]);

  // Handle browser back/refresh with unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = "";
        return "";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [hasUnsavedChanges]);

  // Check if user can edit Margin & RTP section (only SUPER_ADMIN_DELETE)
  const canEditMargins =
    sessionHelperInstance.role === userRoles.SUPER_ADMIN_DELETE;

  // Check if user can edit manual reconciliation setting (only SUPER_ADMIN_DELETE)
  const canEditReconciliation =
    sessionHelperInstance.role === userRoles.SUPER_ADMIN_DELETE;

  // Check if user has permission to view Margin & RTP section
  const canViewMargins = [
    userRoles.SUPER_ADMIN_DELETE,
    userRoles.SUPER_ADMIN,
    userRoles.ADMIN,
  ].includes(sessionHelperInstance.role);

  // Navigate back to operators list, preserving URL params
  const goBackToList = () => {
    // Use navigate(-1) to go back in history, preserving search params
    // Only if we came from the operators list, otherwise navigate to list directly
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate(configData.routes.operators.operators);
    }
  };

  const cancel = () => {
    // Check for unsaved changes before navigating
    if (hasUnsavedChanges) {
      setUnsavedChangesDialog({
        open: true,
        pendingNavigation: goBackToList,
      });
    } else {
      goBackToList();
    }
  };

  // Handle dialog actions for unsaved changes
  const handleUnsavedChangesDiscard = () => {
    setHasUnsavedChanges(false); // Reset to prevent blocker from triggering again
    setUnsavedChangesDialog({ open: false, pendingNavigation: null });
    if (unsavedChangesDialog.pendingNavigation) {
      unsavedChangesDialog.pendingNavigation();
    } else {
      goBackToList();
    }
  };

  const handleUnsavedChangesClose = () => {
    // Reset the blocker if it was blocked
    if (blocker.state === "blocked") {
      blocker.reset();
    }
    setUnsavedChangesDialog({ open: false, pendingNavigation: null });
  };

  // Update initial refs after successful save to reset unsaved changes state
  const updateInitialRefs = () => {
    initialOperatorDetailsRef.current = JSON.parse(
      JSON.stringify(operatorDetails),
    );
    initialVaultConfigRef.current = JSON.parse(
      JSON.stringify(vaultConfigDetails),
    );
    setHasUnsavedChanges(false);
    userHasInteracted.current = false; // Reset interaction flag after save
  };

  // Mark that user has interacted with the form
  const markUserInteraction = () => {
    userHasInteracted.current = true;
  };

  // Section-specific save handlers
  const handleSectionSave = async (sectionType) => {
    try {
      setSectionLoading((prev) => ({ ...prev, [sectionType]: true }));

      // Call the main submit function but indicate it's a section-specific save
      await submit(true);

      // Update refs after successful save
      updateInitialRefs();
    } catch (error) {
      showSnackbar(error.message, "error");
    } finally {
      setSectionLoading((prev) => ({ ...prev, [sectionType]: false }));
    }
  };

  // Instant save for lock toggle
  const handleLockToggleSave = async () => {
    try {
      setLockToggleDialog((prev) => ({ ...prev, loading: true }));

      // Get current lock state and toggle it
      const currentAdminLock =
        operatorDetails?.operatorMargins?.EXTRA_DATA?.isMarginEditingAllowed ??
        false;
      const newAdminLock = !currentAdminLock;

      // Create updated operator details with new lock state
      const updatedOperatorDetails = {
        ...operatorDetails,
        operatorMargins: {
          ...operatorDetails.operatorMargins,
          EXTRA_DATA: {
            ...operatorDetails.operatorMargins?.EXTRA_DATA,
            isMarginEditingAllowed: newAdminLock,
          },
        },
      };

      // Update local state
      setOperatorDetails(updatedOperatorDetails);

      // Call submit with the updated data directly to avoid stale state
      await submitWithData(updatedOperatorDetails, true);

      // Update refs after successful save
      updateInitialRefs();

      // Close dialog
      setLockToggleDialog({ open: false, loading: false });
    } catch (error) {
      // Revert local state on error - keep original state
      showSnackbar(error.message, "error");
      setLockToggleDialog({ open: false, loading: false });
    }
  };

  // Calculate RTP using formula: RTP = 1/(1+Margin)*100
  const calculateRTP = (margin: any) => {
    if (!margin || margin <= 0) return "0.00";
    const rtp = (1 / (1 + margin / 100)) * 100;
    return rtp.toFixed(2);
  };

  // Get validation status for margin data
  const getValidationStatus = (marginValue, market, hasError) => {
    // If there's a validation error, return the error message
    if (hasError) return hasError;

    // If margin data is not set or null, return NA
    if (
      marginValue === "" ||
      marginValue === null ||
      marginValue === undefined
    ) {
      return "NA";
    }

    // Get min/max from default margin data
    const defaultMarginInfo =
      defaultMarginData?.operatorMargins?.EXTRA_DATA?.[market];
    const min = defaultMarginInfo?.min || 0;
    const max = defaultMarginInfo?.max || 100;

    if (marginValue >= min && marginValue <= max) {
      return "true";
    }

    // If value is outside range, return false
    return "false";
  };

  const submitWithData = async (dataToSubmit, skipNavigation = false) => {
    try {
      setLoading(true);

      // Check for validation errors only if adminLock is true (editing enabled)
      const adminLock =
        dataToSubmit.operatorMargins?.EXTRA_DATA?.isMarginEditingAllowed ||
        false;
      if (adminLock && Object.keys(errors).length > 0) {
        throw new Error("Please fix validation errors before saving");
      }

      let response;
      const { operatorMargins, operatorRtp, id: _id, ...rest } = dataToSubmit;

      // Format operatorMargins and operatorRtp for API submission
      const formattedOperatorMargins = operatorMargins
        ? { ...operatorMargins }
        : null;
      const formattedOperatorRtp = operatorRtp ? { ...operatorRtp } : null;

      // Only process if operatorMargins exists
      if (formattedOperatorMargins) {
        // Ensure EXTRA_DATA structure exists with only isMarginEditingAllowed
        formattedOperatorMargins.EXTRA_DATA = {
          isMarginEditingAllowed: adminLock,
        };
      }

      await operatorValidation.validate(rest);

      // Check if any margin fields are filled
      let isAnyFieldFilled = false;
      if (formattedOperatorMargins) {
        Object.keys(formattedOperatorMargins).forEach((market) => {
          if (
            market !== "EXTRA_DATA" &&
            formattedOperatorMargins[market] !== null
          ) {
            isAnyFieldFilled = true;
          }
        });
      }

      if (isAnyFieldFilled && adminLock && formattedOperatorMargins) {
        // Validate only non-null margins
        const marginsToValidate = {};
        Object.keys(formattedOperatorMargins).forEach((market) => {
          if (
            market !== "EXTRA_DATA" &&
            formattedOperatorMargins[market] !== null
          ) {
            marginsToValidate[market] = formattedOperatorMargins[market];
          }
        });
        await marketMarginValidation.validate(marginsToValidate);
      }

      rest.countryName =
        sessionHelperInstance.countries.find(
          (c) => c.isoCode === dataToSubmit.countryCode,
        )?.name ?? null;

      const submissionData = {
        ...rest,
        allowManualReconciliation: rest.allowManualReconciliation ?? false,
        operatorMargins: formattedOperatorMargins,
        operatorRtp: formattedOperatorRtp,
        externalOpId: rest.externalOpId || null,
      };

      if (id === "create") {
        response = await create(submissionData);
        sessionHelperInstance.updatePBO(response?.data, "operator");
      } else {
        // For update, only send the specific fields required by the API
        const updatePayload = {
          operatorName: rest.operatorName,
          platform: rest.platform,
          externalOpId: rest.externalOpId || null,
          operatorMargins: formattedOperatorMargins,
          operatorRtp: formattedOperatorRtp,
          betLimits: rest.betLimits?.map((betLimit) => {
            const { _id, ...betLimitRest } = betLimit;
            return betLimitRest;
          }),
          coinPurchaseAllowed: rest.coinPurchaseAllowed,
          isActive: rest.isActive,
          isUnderMaintenance: rest.isUnderMaintenance,
          underMaintenanceTitle: rest.underMaintenanceTitle,
          underMaintenanceTitleTrans: rest.underMaintenanceTitleTrans,
          showWinLoss: rest.showWinLoss,
          showRtpGameRules: rest.showRtpGameRules,
          countryCode: rest.countryCode,
          countryName: rest.countryName,
          languageCode: rest.languageCode,
          showWalletIcon: rest.showWalletIcon,
          isTutorialVideoEnabled: rest.isTutorialVideoEnabled,
          isStickyCtaEnabled: rest.isStickyCtaEnabled,
          showOddsCalculatingAnimation: rest.showOddsCalculatingAnimation,
          startMatchBtnRestriction: rest.startMatchBtnRestriction ?? false,
          startMatchBtnDiv: rest.startMatchBtnDiv,
          showNewFtue: rest.showNewFtue ?? false,
          allowManualReconciliation: rest.allowManualReconciliation ?? false,
          divisionBaseOvrEnabled: rest.divisionBaseOvrEnabled ?? true,
          matchesForDivisionBaseOvr: rest.matchesForDivisionBaseOvr ?? null,
        };

        response = await update(updatePayload, id);
      }
      showSnackbar(response.message, "success");
      setLoading(false);

      // Only navigate if not skipping navigation (e.g., for lock toggle saves)
      if (!skipNavigation) {
        goBackToList();
      }
    } catch (error) {
      setLoading(false);
      showSnackbar(error.message, "error");
    }
  };

  const submit = async (skipNavigation = false) => {
    return await submitWithData(operatorDetails, skipNavigation);
  };

  const onChange = (e: any) => {
    const { name, value } = e?.target;

    if (name === "adminLock") {
      // Only show confirmation dialog in edit mode, not create mode
      if (id !== "create") {
        setLockToggleDialog({ open: true, loading: false });
      } else {
        // In create mode, directly update the state
        const currentAdminLock =
          operatorDetails?.operatorMargins?.EXTRA_DATA
            ?.isMarginEditingAllowed || false;

        markUserInteraction(); // Mark user interaction
        setOperatorDetails((state) => ({
          ...state,
          operatorMargins: {
            ...state.operatorMargins,
            EXTRA_DATA: {
              ...state.operatorMargins?.EXTRA_DATA,
              isMarginEditingAllowed: !currentAdminLock,
            },
          },
        }));
      }
    } else {
      markUserInteraction(); // Mark user interaction
      setOperatorDetails((state) => {
       const booleanFields = [
          "coinPurchaseAllowed",
          "isActive",
          "isUnderMaintenance",
          "showWinLoss",
          "showRtpGameRules",
          "showWalletIcon",
          "isTutorialVideoEnabled",
          "isStickyCtaEnabled",
          "showOddsCalculatingAnimation",
          "startMatchBtnRestriction",
          "showNewFtue",
          "allowManualReconciliation",
          "divisionBaseOvrEnabled",
        ];

         const numericFields = ["matchesForDivisionBaseOvr"];

        let nextValue;

        if (booleanFields.includes(name)) {
          nextValue = !state[name];
        } else if (numericFields.includes(name)) {
          nextValue =
            value === "" || value === null || value === undefined
              ? null
              : Number(value);
        } else {
          nextValue = value;
        }

        return {
          ...state,
          [name]: nextValue,
        };
      });
    }
  };

  const onVaultConfigChange = (e: any) => {
    const { name, value } = e?.target;

    markUserInteraction(); // Mark user interaction
    setVaultConfigDetails((state) => ({
      ...state,
      [name]: value ? Number(value) : "",
    }));
  };

  const onVaultConfigSave = async () => {
    setVaultConfigSaveLoading(true);
    try {
      const dataToUpdate = {
        operatorId: id,
        baseXP: vaultConfigDetails.baseXP
          ? Number(vaultConfigDetails.baseXP)
          : null,
        growthRate:
          vaultConfigDetails.growthRate !== ""
            ? Number(vaultConfigDetails.growthRate)
            : null,
      };

      const response = await updateOperatorVaultConfig(dataToUpdate);

      // Update refs after successful save
      initialVaultConfigRef.current = JSON.parse(
        JSON.stringify(vaultConfigDetails),
      );
      setHasUnsavedChanges(false);

      showSnackbar(response.message, "success");
    } catch (error) {
      showSnackbar(error.message, "error");
    }
    setVaultConfigSaveLoading(false);
  };

  const handleTranslationChange = (event: any) => {
    const { name, value } = event.target;
    setModalData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleOpenModal = (type, field) => {
    setModalData((state) => {
      if (type === "view") {
        let data = getTranslatedData(field);
        return { ...state, viewData: data, field, modal: type };
      } else {
        return {
          ...state,
          field,
          modal: type,
          language: "",
          inputValue: "",
        };
      }
    });
    open();
  };

  const getTranslatedData = (field: any) => {
    const _translatedData = Object.entries(operatorDetails[field] || {}).map(
      ([key, value]) => ({
        lang: key,
        inputValue: value,
      }),
    );
    if (!_translatedData) return [];
    return _translatedData;
  };

  const handleLangEditClick = (data: any) => {
    close();
    setModalData((state) => ({
      ...state,
      language: data?.lang,
      inputValue: data?.inputValue,
      modal: "add",
    }));
    open();
  };

  const handleLangDeleteClick = (data: any) => {
    if (data.lang === "en") {
      showSnackbar(
        "Cannot delete English translation, as it's a default languange",
        "error",
      );
      return;
    }

    // remove from view data
    setModalData((state) => ({
      ...state,
      viewData: state.viewData.filter((item) => item.lang !== data.lang),
    }));

    markUserInteraction(); // Mark user interaction
    setOperatorDetails((prev) => {
      prev[modalData.field] = Object.keys(prev[modalData.field]).reduce(
        (acc, key) => {
          if (key !== data.lang) {
            acc[key] = prev[modalData.field][key];
          }
          return acc;
        },
        {},
      );
      return { ...prev };
    });
  };

  const handleAddTranslation = () => {
    const { field, inputValue } = modalData;
    if (inputValue === "") {
      showSnackbar("Enter valid translation", "error");
      return;
    }
    if (!field) {
      showSnackbar("Confused which field to add the translation", "error");
      return;
    }

    markUserInteraction(); // Mark user interaction
    setOperatorDetails((prev) => ({
      ...prev,
      [field]: {
        ...prev[field],
        [modalData.language]: modalData.inputValue,
      },
    }));

    // if english translation is available set it field
    if (modalData.language === "en") {
      setOperatorDetails((details) => ({
        ...details,
        [field?.split("Trans")[0]]: modalData.inputValue,
        [field]: { ...details[field], en: modalData.inputValue },
      }));
    }
    setModalData((prev) => ({
      ...prev,
      inputValue: "",
      language: "",
      field: null,
    }));
    close();
  };

  const onMarketChange = async (e, type) => {
    const { name, value } = e?.target;

    markUserInteraction(); // Mark user interaction

    // Handle empty string differently from 0
    let processedValue;
    if (value === "" || value === null || value === undefined) {
      processedValue = null; // Use null instead of empty string for API consistency
    } else {
      processedValue = type === "operatorMargins" ? Number(value) : value;
    }

    if (type === "operatorMargins") {
      // Ensure operatorMargins exists
      if (!operatorDetails.operatorMargins) {
        setOperatorDetails((state) => ({
          ...state,
          operatorMargins: {},
        }));
        return;
      }

      // Get min/max values from default margin data for validation
      const defaultMarginInfo =
        defaultMarginData?.operatorMargins?.EXTRA_DATA?.[name];
      const min = defaultMarginInfo?.min || 0;
      const max = defaultMarginInfo?.max || 100;

      // Validate margin value only if it's not null
      if (
        processedValue !== null &&
        (processedValue < min || processedValue > max)
      ) {
        setErrors((prev) => ({
          ...prev,
          [name]: `Margin value must be between ${min} and ${max}`,
        }));
      } else {
        setErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors[name];
          return newErrors;
        });
      }

      // Update margin value in operatorMargins
      setOperatorDetails((state) => ({
        ...state,
        operatorMargins: {
          ...state.operatorMargins,
          [name]: processedValue,
        },
      }));

      // Calculate RTP if margin value is valid and not null
      if (
        processedValue !== null &&
        processedValue >= min &&
        processedValue <= max
      ) {
        const newRtp = calculateRTP(processedValue);

        // Update RTP values
        const newRtpValue = {
          ...operatorDetails.operatorRtp,
          [name]: newRtp,
        };

        // Calculate overall RTP
        const overallRtp = calculateOverallRTP(newRtpValue);
        newRtpValue.OVER_ALL = overallRtp;

        setOperatorDetails((state) => ({
          ...state,
          operatorRtp: newRtpValue,
        }));
      } else if (processedValue === null) {
        // If margin is set to null, use default RTP from defaultMarginData
        const defaultRtp = defaultMarginData?.operatorRtp?.[name];
        const newRtpValue = {
          ...operatorDetails.operatorRtp,
          [name]: defaultRtp || "0.00",
        };

        // Calculate overall RTP
        const overallRtp = calculateOverallRTP(newRtpValue);
        newRtpValue.OVER_ALL = overallRtp;

        setOperatorDetails((state) => ({
          ...state,
          operatorRtp: newRtpValue,
        }));
      }
    } else {
      // For other fields, use original logic
      setOperatorDetails((state) => ({
        ...state,
        [type]: { ...state[type], [name]: processedValue },
      }));
    }
  };

  const fetchData = async () => {
    setInitialLoading(true);
    try {
      let promises = [getBettingMarkets(), getDefaultMarginInfo()];

      // For edit operator, also fetch operator info and configurations
      if (id !== "create") {
        promises.push(getOperatorInfo(id), getConfigurations());
      }

      const responses = await Promise.all(promises);
      const [marketResp, defaultMarginResp, ...otherResponses] = responses;

      // Store default margin data for validation
      if (defaultMarginResp?.data) {
        setDefaultMarginData(defaultMarginResp.data);
      }

      // Variables to capture final state for initial refs
      let finalOperatorDetails = { ..._operatorDetails };
      let finalVaultConfig = { ..._vaultConfigDetails };

      // Handle create operator case with default margins
      if (id === "create") {
        if (defaultMarginResp?.data) {
          const { operatorMargins, operatorRtp } = defaultMarginResp.data;

          finalOperatorDetails = {
            ...finalOperatorDetails,
            operatorMargins: operatorMargins || null,
            operatorRtp: operatorRtp || null,
          };

          // Set default margins and RTPs from API
          setOperatorDetails(finalOperatorDetails);
        }
      } else {
        // Handle edit operator case
        const [operatorInfo, miscConfig] = otherResponses;

        if (operatorInfo?.data) {
          const _underMaintenanceTitleTrans = {
            ...operatorInfo?.data?.underMaintenanceTitleTrans,
          };

          if (
            operatorInfo?.data?.underMaintenanceTitle &&
            !_underMaintenanceTitleTrans["en"]
          ) {
            _underMaintenanceTitleTrans["en"] =
              operatorInfo.data?.underMaintenanceTitle;
          } else {
            operatorInfo.data.underMaintenanceTitle =
              _underMaintenanceTitleTrans["en"];
          }

          // Ensure EXTRA_DATA exists and has isMarginEditingAllowed, default to false if not present
          let operatorMargins = operatorInfo?.data?.operatorMargins;
          if (!operatorMargins) {
            operatorMargins = {
              ...(defaultMarginResp?.data?.operatorMargins ?? {}),
              EXTRA_DATA: { isMarginEditingAllowed: false },
            };
          }

          // Use operator's existing margins and RTP directly from API
          const { allowManualReconciliation: _allowManualReconciliation, ...restOperatorInfo } = operatorInfo?.data ?? {};
          finalOperatorDetails = {
            ..._operatorDetails,
            ...restOperatorInfo,
            allowManualReconciliation: _allowManualReconciliation ?? false,
            operatorMargins: operatorMargins,
            operatorRtp:
              operatorInfo?.data?.operatorRtp ??
              defaultMarginResp?.data?.operatorRtp,
            underMaintenanceTitleTrans: _underMaintenanceTitleTrans,
            // Default to enabled (current matchmaking behaviour) for operators that
            // pre-date this field, so the toggle never renders as unintentionally off.
            divisionBaseOvrEnabled:
              operatorInfo?.data?.divisionBaseOvrEnabled ?? true,
          };
          setOperatorDetails(finalOperatorDetails);
        }

        if (miscConfig?.data?.length) {
          const vaultConfig = miscConfig.data.find(
            (d) => d.type === "VAULT_CONFIGURATION",
          );

          const operatorVaultConfig = vaultConfig?.data?.operatorCnf;

          if (operatorVaultConfig) {
            const specificVaultConfig = operatorVaultConfig[id];

            if (specificVaultConfig) {
              finalVaultConfig = {
                baseXP: specificVaultConfig.baseXP || "",
                growthRate: specificVaultConfig.growthRate ?? "",
              };
              setVaultConfigDetails(finalVaultConfig);
            }
          }
        }
      }

      // Store initial values for unsaved changes detection
      initialOperatorDetailsRef.current = JSON.parse(
        JSON.stringify(finalOperatorDetails),
      );
      initialVaultConfigRef.current = JSON.parse(
        JSON.stringify(finalVaultConfig),
      );

      setOptions((state) => {
        return {
          ...state,
          bettingMarkets: [...marketResp?.data?.markets, "OVER_ALL"],
        };
      });

      // Mark data as loaded after everything is set
      isDataLoaded.current = true;
    } catch (error) {
      showSnackbar(error.message, "error");
    } finally {
      setInitialLoading(false);
    }
  };

  const addBetLimit = () => {
    markUserInteraction(); // Mark user interaction
    setOperatorDetails((prevState) => ({
      ...prevState,
      betLimits: [_betLimit, ...prevState.betLimits],
    }));
  };

  const removeAll = () => {
    markUserInteraction(); // Mark user interaction
    setOperatorDetails((prevState) => ({ ...prevState, betLimits: [] }));
  };

  const onRemoveBetLimit = (index: any) => {
    markUserInteraction(); // Mark user interaction
    setOperatorDetails((prevState) => ({
      ...prevState,
      betLimits: prevState.betLimits.filter((_, idx) => idx !== index),
    }));
  };

  const onBetLimitChange = (e, index) => {
    const { name, value } = e?.target;
    markUserInteraction(); // Mark user interaction
    setOperatorDetails((prevState) => {
      return {
        ...prevState,
        betLimits: [
          ...prevState.betLimits.slice(0, index),
          {
            ...prevState.betLimits[index],
            [name]: value,
          },
          ...prevState.betLimits.slice(index + 1),
        ],
      };
    });
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Show skeleton while initial data is loading
  if (initialLoading) {
    return (
      <ContentShell
        showBackButton
        onBackButtonClick={() => navigate(-1)}
        title={`${id === "create" ? "Create" : "Edit"} Operator`}
        loading={false}
      >
        <OperatorEditorSkeleton />
      </ContentShell>
    );
  }

  return (
    <ContentShell
      showBackButton
      onBackButtonClick={cancel}
      title={`${id === "create" ? "Create" : "Edit"} Operator`}
      loading={loading}
      actions={
        <Box className={styles.buttonContainer}>
          {hasUnsavedChanges && (
            <Typography
              variant="caption"
              sx={{
                color: "#f57c00",
                display: "flex",
                alignItems: "center",
                mr: 2,
                fontWeight: 500,
              }}
            >
              ⚠️ Unsaved changes
            </Typography>
          )}
          <Button
            type="button"
            variant="outlined"
            onClick={cancel}
            className={styles.cancelButton}
          >
            Cancel
          </Button>
          {id === "create" && (
            <Button
              type="submit"
              variant="contained"
              onClick={() => {
                submit(false);
              }}
              disabled={loading}
            >
              {loading ? "Creating..." : "Create Operator"}
            </Button>
          )}
        </Box>
      }
    >
      <Box className={styles.mainContainer}>
        {/* Operator Information & Location Settings */}
        <OperatorInformation
          operatorDetails={operatorDetails}
          onChange={onChange}
          onSave={
            id !== "create" ? () => handleSectionSave("information") : undefined
          }
          loading={sectionLoading.information}
        />

        {/* Features & Settings Section */}
        <FeaturesSettings
          operatorDetails={operatorDetails}
          onChange={onChange}
          handleOpenModal={handleOpenModal}
          onSave={
            id !== "create" ? () => handleSectionSave("features") : undefined
          }
          loading={sectionLoading.features}
          canEditReconciliation={canEditReconciliation}
        />

        {/* Vault Configuration Section */}
        {id !== "create" && (
          <VaultConfiguration
            vaultConfigDetails={vaultConfigDetails}
            onVaultConfigChange={onVaultConfigChange}
            onVaultConfigSave={onVaultConfigSave}
            vaultConfigSaveLoading={vaultConfigSaveLoading}
          />
        )}

        <Divider className={styles.simpleDivider} />

        {/* Market Margins Section - Role-based Access */}
        {canViewMargins ? (
          <Box>
            {/* Enhanced Admin Lock Toggle */}
            <MarginLockToggle
              operatorDetails={operatorDetails}
              onChange={onChange}
              canEditMargins={canEditMargins}
            />
            {/* Controlled Sections Container */}
            <Box
              sx={{
                position: "relative",
                border: operatorDetails?.operatorMargins?.EXTRA_DATA
                  ?.isMarginEditingAllowed
                  ? "2px dashed #4caf50"
                  : "2px dashed #ff9800",
                borderRadius: 3,
                p: 2,
                backgroundColor: operatorDetails?.operatorMargins?.EXTRA_DATA
                  ?.isMarginEditingAllowed
                  ? "rgba(76, 175, 80, 0.02)"
                  : "rgba(255, 152, 0, 0.02)",
                transition: "all 0.3s ease",
                "&::before": {
                  content: `"${
                    operatorDetails?.operatorMargins?.EXTRA_DATA
                      ?.isMarginEditingAllowed
                      ? "🔓 EDITING ENABLED"
                      : "🔒 LOCKED"
                  }"`,
                  position: "absolute",
                  top: -12,
                  left: 20,
                  backgroundColor: "white",
                  padding: "4px 12px",
                  fontSize: "12px",
                  fontWeight: "bold",
                  color: operatorDetails?.operatorMargins?.EXTRA_DATA
                    ?.isMarginEditingAllowed
                    ? "#2e7d32"
                    : "#f57c00",
                  borderRadius: "8px",
                  border: operatorDetails?.operatorMargins?.EXTRA_DATA
                    ?.isMarginEditingAllowed
                    ? "1px solid #4caf50"
                    : "1px solid #ff9800",
                },
              }}
            >
              {/* Bet Limits */}
              <BetLimitsSection
                operatorDetails={operatorDetails}
                addBetLimit={addBetLimit}
                removeAll={removeAll}
                onRemoveBetLimit={onRemoveBetLimit}
                onBetLimitChange={onBetLimitChange}
                canEditMargins={canEditMargins}
                onSave={
                  id !== "create"
                    ? () => handleSectionSave("betLimits")
                    : undefined
                }
                loading={sectionLoading.betLimits}
              />

              <MarginRTPSection
                operatorDetails={operatorDetails}
                options={options}
                errors={errors}
                onMarketChange={onMarketChange}
                getValidationStatus={getValidationStatus}
                defaultMarginData={defaultMarginData}
                canEditMargins={canEditMargins}
                onSave={
                  id !== "create"
                    ? () => handleSectionSave("margins")
                    : undefined
                }
                loading={sectionLoading.margins}
              />
            </Box>{" "}
            {/* Close Controlled Sections Container */}
          </Box>
        ) : (
          <Box>
            <Typography className={styles.accessDeniedTitle}>
              Margin & RTP
            </Typography>
            <Alert severity="warning" className={styles.accessDeniedAlert}>
              You do not have permission to view this section.
            </Alert>
          </Box>
        )}
      </Box>
      <ViewLanguageModal
        isMultiline={true}
        label="Select Language"
        isOpen={isOpen}
        close={close}
        onChange={handleTranslationChange}
        handleOnSubmit={handleAddTranslation}
        actionHandlers={{
          handleLangEditClick: handleLangEditClick,
          handleLangDeleteClick: handleLangDeleteClick,
        }}
        modalData={modalData}
        tableColumns={[
          {
            id: "lang",
            label: "Language",
            alignHead: "center",
            alignBody: "center",
          },
          {
            id: "inputValue",
            label: "Translation",
            alignBody: "center",
            alignHead: "center",
          },
          {
            id: "actions",
            label: "Actions",
            alignHead: "center",
            alignBody: "center",
          },
        ]}
      />

      {/* Lock Toggle Confirmation Dialog */}
      <LockToggleConfirmDialog
        open={lockToggleDialog.open}
        onClose={() => setLockToggleDialog({ open: false, loading: false })}
        onConfirm={handleLockToggleSave}
        isCurrentlyLocked={
          operatorDetails?.operatorMargins?.EXTRA_DATA
            ?.isMarginEditingAllowed || false
        }
        loading={lockToggleDialog.loading}
      />

      {/* Unsaved Changes Confirmation Dialog */}
      <UnsavedChangesDialog
        open={unsavedChangesDialog.open}
        onClose={handleUnsavedChangesClose}
        onDiscard={handleUnsavedChangesDiscard}
        message="You have unsaved changes in the operator settings. Are you sure you want to leave this page? Your changes will be lost."
      />
    </ContentShell>
  );
};

export default OperatorEditor;
